import { addHours } from '@luna/date';

const ONE_HOUR_AGO = -1;
const YEAR_HOURS_AGO = 8760;

function getCookieExpiryDate(hours: number): string {
    const exdate = addHours(new Date(), hours);
    if (hours) {
        return `expires=${exdate.toUTCString()};`;
    }

    return '';
}

function createCookiePair(key: string, value = ''): string {
    return `${key}=${encodeURIComponent(value)};`;
}

function getPath(path?: string): string {
    if (!path) {
        return '';
    }

    return `path=${path};`;
}

/**
 * Sets a cookie with the given key, value, and expiration time. By default, the cookie will be valid for one hour if no expiration time is provided.
 * The cookie can only be accessed on HTTPS unless the __DEV__ variable is true in development environment.
 *
 * @param key - The key of the cookie to set.
 * @param value - The value of the cookie to set.
 * @param [hours=0] - The number of hours until the cookie expires (0 for no expiration). Defaults to 1 hour.
 * @param [path='/'] - The path on which the cookie is valid (defaults to '/').
 *
 * @example
 * import { setCookie } from '@luna/cookies';
 *
 * setCookie("ph", "val", 1 "/");
 */
export function setCookie(key: string, value: string, hours = 0, path?: string): void {
    /**
     * Putting secure flag on localhost breaks safari
     */
    document.cookie = `${createCookiePair(key, value)}${getCookieExpiryDate(hours)}${getPath(
        path,
    )}${!__DEV__ ? 'secure;' : ''}`;
}

/**
 * Deletes the cookie - resets it to the empty string and -1 expiration.
 * @param key key of the cookie.
 *
 * @example
 * import { deleteCookie } from '@luna/cookies';
 *
 * deleteCookie("ph");
 */
export function deleteCookie(key: string): void {
    setCookie(key, '', ONE_HOUR_AGO);
}

/**
 * Retrieves the value of a cookie with the given key.
 *
 * @param key - The key of the cookie to retrieve.
 * @returns The value of the cookie if it exists, otherwise an empty string.
 *
 * @example
 * import { getCookie } from '@luna/cookies';
 *
 * const value = getCookie("ph");
 */
export function getCookie(key: string): string {
    const cookies = document.cookie.split(';');
    const cookie = cookies.find((c) => {
        const [cookieName] = c.split('=');
        return cookieName.trim() === key;
    });
    return cookie ? cookie.split('=')[1] : '';
}

/**
 * Deletes all cookies by setting their values to empty strings and expiration times to one year ago.
 *
 * @example
 * import { deleteAllCookies } from '@luna/cookies';
 *
 * deleteAllCookies();
 */
export function deleteAllCookies(): void {
    const cookies = document.cookie.split('; ');

    for (let i = 0; i < cookies.length; i += 1) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.slice(0, eqPos) : cookie;
        setCookie(name, '', -YEAR_HOURS_AGO);
    }
}
